(() => {
  if (customElements.get('sbc-mobile-search')) return;

  class SbcMobileSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.initial = this.querySelector('[data-search-initial]');
      this.clear = this.querySelector('[data-search-clear]');
      this.bottom = this.querySelector('[data-search-bottom]');
      this.all = this.querySelector('[data-search-all]');
      this.cache = new Map();
      this.revision = 0;
      this.mobile = matchMedia('(max-width: 749px)');
      this.events = new AbortController();
      const options = {signal: this.events.signal};

      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-sbc-mobile-search-open]');
        if (!trigger || trigger.dataset.sbcMobileSearchOpen !== this.id || !this.mobile.matches) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.open(trigger);
      }, options);

      this.querySelector('[data-search-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientY > bounds.bottom || event.clientY < bounds.top || event.clientX < bounds.left || event.clientX > bounds.right) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.closed(), options);
      this.input.addEventListener('input', () => this.update(), options);
      this.input.addEventListener('search', () => this.update(), options);
      this.input.addEventListener('compositionstart', () => { this.composing = true; }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.update(); }, options);
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.update();
        this.input.focus({preventScroll: true});
      }, options);
      this.addEventListener('click', event => {
        const chip = event.target.closest('[data-search-query]');
        if (!chip) return;
        this.input.value = chip.dataset.searchQuery;
        this.update(true);
        this.input.focus({preventScroll: true});
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.input.value.trim()) return;
        event.preventDefault();
        this.input.focus({preventScroll: true});
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = [...this.results.querySelectorAll('[data-search-result]')];
        if (!links.length) return;
        const active = document.activeElement;
        const index = links.indexOf(active);
        if (active !== this.input && index < 0) return;
        event.preventDefault();
        if (event.key === 'ArrowUp' && index <= 0) this.input.focus({preventScroll: true});
        else links[(index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
      }, options);
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches && this.dialog.open) this.dialog.close();
      }, options);
      window.addEventListener('pagehide', () => {
        if (this.dialog.open) this.dialog.close();
      }, options);
    }

    open(trigger) {
      if (this.dialog.open) return;
      const menu = document.querySelector('[data-menu-toggle][aria-expanded="true"]');
      if (menu) menu.click();
      this.trigger = trigger;
      this.scrollPosition = window.scrollY;
      this.dialog.showModal();
      document.documentElement.classList.add('sbc-mobile-search-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.input.focus({preventScroll: true});
      this.update(true);
    }

    closed() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.revision += 1;
      document.documentElement.classList.remove('sbc-mobile-search-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.trigger?.isConnected && this.mobile.matches) this.trigger.focus({preventScroll: true});
      if (typeof this.scrollPosition === 'number' && Math.abs(window.scrollY - this.scrollPosition) > 1) {
        window.scrollTo({top: this.scrollPosition, behavior: 'instant'});
      }
    }

    update(immediate = false) {
      if (this.composing) return;
      clearTimeout(this.timer);
      this.request?.abort();
      const revision = ++this.revision;
      const query = this.input.value.trim();
      this.clear.hidden = !query;
      this.initial.hidden = Boolean(query);
      this.bottom.hidden = !query;
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      this.all.href = url.href;
      if (query.length < 2) {
        this.status.textContent = query ? this.dataset.minimum : '';
        return;
      }
      const cached = this.cache.get(query.toLocaleLowerCase());
      if (cached) {
        this.render(cached);
        return;
      }
      this.status.textContent = this.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.fetchResults(query, revision), immediate ? 0 : 180);
    }

    async fetchResults(query, revision) {
      const controller = new AbortController();
      this.request = controller;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 7000);
      try {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'sbc-mobile-search-results');
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url, {signal: controller.signal, headers: {Accept: 'text/html'}});
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (revision !== this.revision || !this.dialog.open) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mobile-search-response]');
        if (!content) throw new Error('Search response missing');
        const entry = {html: content.innerHTML, message: content.dataset.resultMessage};
        const key = query.toLocaleLowerCase();
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, entry);
        this.render(entry);
      } catch (error) {
        if (revision !== this.revision || !this.dialog.open) return;
        if (error.name !== 'AbortError' || timedOut) {
          this.results.replaceChildren();
          this.status.textContent = this.dataset.error;
        }
      } finally {
        clearTimeout(timeout);
        if (revision === this.revision) this.results.removeAttribute('aria-busy');
      }
    }

    render(entry) {
      this.results.innerHTML = entry.html;
      this.results.removeAttribute('aria-busy');
      this.status.textContent = entry.message;
    }

    disconnectedCallback() {
      this.closed();
      this.events?.abort();
      this.ready = false;
    }
  }

  customElements.define('sbc-mobile-search', SbcMobileSearch);
})();
